import type { UserListItem } from '../../administration/users/userTypes';
import { APPROVAL_DECISION, APPROVAL_MODE } from '../approvalTypes';
import type { ApprovalApproverInput, ApprovalWorkflowOption } from '../approvalTypes';

/**
 * A submittable routing configuration, resolved against the LIVE catalogs.
 *
 * **Both fields are foreign keys, so both are picked from what the form already loaded** —
 * a generated workflow key or account id the server does not hold fails its own existence
 * check, which reads as a bug in the form rather than in the generator.
 */
export function sampleRouting({
  workflows,
  accounts,
  lockedWorkflowKey,
}: {
  workflows: ApprovalWorkflowOption[];
  accounts: UserListItem[];
  /** Set on an EDIT, where the workflow is stated rather than offered. */
  lockedWorkflowKey: string | null;
}): { workflowKey: string | null; approvers: ApprovalApproverInput[] } {
  /*
    NEVER A FIELD THE READER HAS ALREADY DECIDED. On an edit the workflow is stated rather than
    offered, so the generator leaves it exactly where it is; on a create it prefers a workflow
    nobody has configured yet, because configuring one twice is the request the server refuses.
  */
  const workflowKey =
    lockedWorkflowKey ??
    (workflows.find((w) => !w.isConfigured) ?? workflows[0])?.key ??
    null;

  /*
    SEQUENTIAL and APPROVE — the ordinary chain: one at a time, authorizing. A generator that
    picked a mode at random would produce a different form on every click, which is worse than
    one that fills less.
  */
  const approvers = accounts
    .slice(0, 1)
    .map((a) => ({
      userId: a.id,
      mode: APPROVAL_MODE.sequential,
      decisionType: APPROVAL_DECISION.approve,
    }));

  return { workflowKey, approvers };
}
